import React from "react";
import {
  BrowserRouter,
  Routes as RouterRoutes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { LanguageProvider } from "./context/LanguageContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import { Navbar, Footer } from "./components/Layout";
import LiveChat from "./components/LiveChat/LiveChat";
import { SkeletonPage } from "./components/ui/Skeleton";

// Pages — lazy-loaded for code splitting
import { Suspense, lazy } from "react";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import NotFound from "./pages/NotFound";

const AcademyPage = lazy(() => import("./pages/AcademyPage"));
const TradePage = lazy(() => import("./pages/TradePage"));
const VaultPage = lazy(() => import("./pages/VaultPage"));
const TestimonialsPage = lazy(() => import("./pages/TestimonialsPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const HelpCenterPage = lazy(() => import("./pages/HelpCenterPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const AdminPage = lazy(() => import("./pages/admin"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const Dashboard = lazy(() => import("./pages/dashboard/index"));
const AdminChatPWA = lazy(() => import("./pages/admin/chat/AdminChatPWA"));

/**
 * Enhanced Protected Route
 * Redirects guests to login and non-admins away from admin tools.
 */
const ProtectedRoute = ({
  children,
  adminOnly = false,
}: {
  children: JSX.Element;
  adminOnly?: boolean;
}) => {
  const { isAuthenticated, isAdmin, isInitializing } = useAuth();
  const location = useLocation();

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-background p-6">
        <SkeletonPage />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/**
 * Layout Wrapper
 * Handles Navbar/Footer visibility.
 * Note: Dashboard and Admin usually have their own custom sidebars/headers.
 */
const LayoutWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  // We hide the standard Navbar on Dashboard, Admin, and Auth pages to use custom headers
  const hideChromeRoutes = ["/admin", "/login", "/register", "/reset-password", "/dashboard"];
  const isDashboardRoute =
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/admin");
  const showChrome =
    !hideChromeRoutes.includes(location.pathname) && !isDashboardRoute;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {showChrome && <Navbar />}
      <main className="flex-grow">{children}</main>
      {showChrome && <Footer />}
      {/* LiveChat widget on public & auth pages — dashboard has its own via UserLayout */}
      {showChrome && <LiveChat />}
    </div>
  );
};

export function AppRoutes() {
  const { isInitializing, isAuthenticated, isAdmin } = useAuth();

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <LayoutWrapper>
          <Suspense fallback={<div className="min-h-screen bg-background p-6"><SkeletonPage /></div>}>
          <RouterRoutes>
            {/* --- PUBLIC ROUTES (accessible to everyone) --- */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/trade" element={<TradePage />} />
            <Route path="/vault" element={<VaultPage />} />
            <Route path="/academy" element={<AcademyPage />} />
            <Route path="/help" element={<HelpCenterPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />

            {/* --- AUTH ROUTES --- */}
            <Route
              path="/login"
              element={
                !isInitializing && isAuthenticated ? (
                  <Navigate to={isAdmin ? "/admin" : "/dashboard"} replace />
                ) : (
                  <LoginPage />
                )
              }
            />
            <Route
              path="/register"
              element={
                !isInitializing && isAuthenticated ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <RegisterPage />
                )
              }
            />

            {/* --- PASSWORD RESET --- */}
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* --- PROTECTED ROUTES (auth required) --- */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* --- PWA ADMIN CHAT (dedicated full-screen chat) --- */}
            <Route
              path="/admin/chat"
              element={
                <ProtectedRoute adminOnly>
                  <AdminChatPWA />
                </ProtectedRoute>
              }
            />

            {/* --- PROTECTED ADMIN ROUTES --- */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminPage />
                </ProtectedRoute>
              }
            />

            {/* --- LEGACY & REDIRECTS --- */}
            <Route
              path="/wallet"
              element={<Navigate to="/dashboard?section=deposit" replace />}
            />
            <Route
              path="/crypto-deposits-withdrawals"
              element={<Navigate to="/dashboard?section=deposit" replace />}
            />

            {/* --- 404 --- */}
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
          </Suspense>
        </LayoutWrapper>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <CurrencyProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </CurrencyProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}
